import React, { useState, useEffect } from "react";
import {
  GoogleMap,
  Autocomplete,
  useJsApiLoader,
} from "@react-google-maps/api";

const libraries: "places"[] = ["places"];

interface LocationPickerProps {
  value: string;
  onChange: (location: { address: string; lat: number; lng: number }) => void;
}

const LocationPicker: React.FC<LocationPickerProps> = ({ value, onChange }) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [markerPosition, setMarkerPosition] =
    useState<google.maps.LatLngLiteral | null>(null);
  const [searchBox, setSearchBox] =
    useState<google.maps.places.Autocomplete | null>(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries: libraries,
  });

  useEffect(() => {
    if (isLoaded && map && markerPosition) {
      (async () => {
        const markerModule = (await google.maps.importLibrary(
          "marker"
        )) as google.maps.MarkerLibrary;
        const { AdvancedMarkerElement } = markerModule;
        new AdvancedMarkerElement({
          map: map,
          position: markerPosition,
        });
      })();
    }
  }, [isLoaded, map, markerPosition]);

  useEffect(() => {
    if (value && map && !markerPosition) {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: value }, (results, status) => {
        if (
          status === google.maps.GeocoderStatus.OK &&
          results &&
          results[0].geometry.location
        ) {
          const location = results[0].geometry.location;
          const lat = location.lat();
          const lng = location.lng();
          setMarkerPosition({ lat, lng });
          map.panTo({ lat, lng });
        }
      });
    }
  }, [value, map, markerPosition]);

  const onMapLoad = (mapInstance: google.maps.Map) => {
    setMap(mapInstance);
  };

  const onPlacesChanged = () => {
    const place = searchBox?.getPlace();
    if (place && place.geometry && place.geometry.location) {
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      const address = place.formatted_address || "";
      setMarkerPosition({ lat, lng });
      onChange({ address, lat, lng });
      map?.panTo({ lat, lng });
    }
  };

  if (!isLoaded) return <div>Loading...</div>;

  return (
    <div className="w-full h-96 relative">
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        zoom={10}
        onLoad={onMapLoad}
        center={markerPosition || { lat: -34.397, lng: 150.644 }}
        options={{
          mapId: "DEMO_MAP_ID",
          fullscreenControl: false,
          mapTypeControl: false,
        }}
      >
        <Autocomplete
          onLoad={(autocomplete) => setSearchBox(autocomplete)}
          onPlaceChanged={onPlacesChanged}
        >
          <input
            type="text"
            placeholder="Search for a location"
            className="absolute top-2 left-1/2 transform -translate-x-1/2 w-3/4 p-2 border rounded z-10"
          />
        </Autocomplete>
      </GoogleMap>
    </div>
  );
};

export default LocationPicker;
